/**
 * 主题偏好与对比度。
 *
 * 这里只放**纯逻辑**（可单测、不碰 DOM），DOM 副作用在同目录的 `useTheme.ts`。
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';
export type ContrastPreference = 'normal' | 'high';

export const THEME_STORAGE_KEY = 'hgc.theme';
export const CONTRAST_STORAGE_KEY = 'hgc.contrast';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['light', 'dark', 'system'];
export const CONTRAST_PREFERENCES: readonly ContrastPreference[] = ['normal', 'high'];

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

export function isContrastPreference(value: unknown): value is ContrastPreference {
  return typeof value === 'string' && (CONTRAST_PREFERENCES as readonly string[]).includes(value);
}

/**
 * `system` 需要外部把 `prefers-color-scheme` 的结果传进来 ——
 * 这样这个函数保持纯粹，测试不需要伪造 `matchMedia`。
 */
export function resolveTheme(preference: ThemePreference, systemPrefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return systemPrefersDark ? 'dark' : 'light';
  return preference;
}

/**
 * 应用主题到根元素时要写的三样东西。
 *
 * 之所以返回描述而不是直接改 DOM：调用方（React 或首屏脚本）各自决定怎么写，
 * 而"该写什么"这件事只有一处定义 —— 首屏内联脚本要复刻的正是这份描述。
 */
export interface ThemeAttributes {
  /** 是否给 `<html>` 加 `dark` 类 */
  dark: boolean;
  /** `data-contrast` 的值；`normal` 时调用方应移除该属性 */
  contrast: ContrastPreference;
  /** 与 `data-contrast` 配套，供 CSS 选择器用 */
  colorScheme: 'light' | 'dark';
}

export function themeAttributes(
  preference: ThemePreference,
  contrast: ContrastPreference,
  systemPrefersDark: boolean,
): ThemeAttributes {
  const resolved = resolveTheme(preference, systemPrefersDark);
  return { dark: resolved === 'dark', contrast, colorScheme: resolved };
}

/** 主题或对比度为 `system` 时需要跟随系统变化（对比度没有 system，这里只为将来的扩展留口） */
export function followsSystem(preference: ThemePreference): boolean {
  return preference === 'system';
}
